import type { BrowserKind, DeviceExtension, DeviceRecord, InstalledExtension } from '@bsync/shared';
import type { BsyncClient } from './client';
import { toRemoteError } from './errors';
import {
  deviceExtensionFromDb,
  deviceFromDb,
  extensionToDb,
  type DbDevice,
  type DbDeviceExtension,
} from './mapping';

export interface DeviceRegistration {
  name: string;
  browser: BrowserKind;
  platform?: string | null;
  userAgent?: string | null;
  appVersion?: string | null;
  /** Previously assigned id; refreshed if it still exists, otherwise a new device is created. */
  existingId?: string | null;
}

export async function registerDevice(client: BsyncClient, reg: DeviceRegistration): Promise<DeviceRecord> {
  const { data, error, status } = await client.rpc('register_device', {
    p_name: reg.name,
    p_browser: reg.browser,
    p_platform: reg.platform ?? null,
    p_user_agent: reg.userAgent ?? null,
    p_app_version: reg.appVersion ?? null,
    p_device_id: reg.existingId ?? null,
  });
  if (error) throw toRemoteError(error, status);
  return deviceFromDb(data as DbDevice);
}

export async function listDevices(client: BsyncClient): Promise<DeviceRecord[]> {
  const { data, error, status } = await client.from('devices').select('*').order('created_at');
  if (error) throw toRemoteError(error, status);
  return (data as DbDevice[]).map(deviceFromDb);
}

export async function renameDevice(client: BsyncClient, id: string, name: string): Promise<void> {
  const clean = name.trim().slice(0, 80);
  if (!clean) throw new Error('Device name cannot be empty.');
  const { error, status } = await client.from('devices').update({ name: clean }).eq('id', id);
  if (error) throw toRemoteError(error, status);
}

/** Disconnect a device. Its bookmarks stay; it stops syncing and must sign in again. */
export async function disconnectDevice(client: BsyncClient, id: string): Promise<void> {
  const { error, status } = await client.from('devices').delete().eq('id', id);
  if (error) throw toRemoteError(error, status);
}

export async function reportExtensions(client: BsyncClient, deviceId: string, extensions: InstalledExtension[]): Promise<void> {
  const { error, status } = await client.rpc('report_extensions', {
    p_device_id: deviceId,
    p_extensions: extensions.map(extensionToDb),
  });
  if (error) throw toRemoteError(error, status);
}

export async function listDeviceExtensions(client: BsyncClient): Promise<DeviceExtension[]> {
  const { data, error, status } = await client.from('device_extensions').select('*').order('name');
  if (error) throw toRemoteError(error, status);
  return (data as DbDeviceExtension[]).map(deviceExtensionFromDb);
}
